/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import Bell from '@react/react-spectrum/Icon/Bell';
import PropTypes from 'prop-types';
import React from 'react';
import StatusLight from '@react/react-spectrum/StatusLight';
import './BadgeIcon.scss';

/**
 * Check value count and convert to string for display.
 * @param {number} value The notifications counter.
 * @param {number} maxDigits Number of digits to display notification
 * before adding a '+'.
 * @returns {string} Value as string.
 */
const computeDisplayValue = (value, maxDigits) => {
  // If the value is negative or 0 we don't display the badge
  if (value <= 0) {
    return '';
  }
  const maxValue = Math.pow(10, maxDigits) - 1;

  return value > maxValue ? `${maxValue}+` : `${value}`;
};

function BadgeIcon(props) {
  const {count, maxDigits, hasNewAnnouncement} = props;
  const notificationCount = computeDisplayValue(count, maxDigits);

  return (
    <div className="shell-Badge-Icon">
      {notificationCount && (
        <span className="shell-Badge">
          {notificationCount}
        </span>
      )}

      {!notificationCount && hasNewAnnouncement && (
        <StatusLight className="shell-status-light" variant="info" />
      )}
      <Bell size="S" />
    </div>
  );
}

BadgeIcon.defaultProps = {
  count: 0,
  hasNewAnnouncement: false,
  maxDigits: 2
};

BadgeIcon.propTypes = {
  /** Number of notification counts. */
  count: PropTypes.number,
  /** Flag to determine if announcement statuslight is shown */
  hasNewAnnouncement: PropTypes.bool,
  /** The number of digits to display before the adding the + sign. */
  maxDigits: PropTypes.number
};

export default React.memo(BadgeIcon);
